import {
  CompletionRange,
  FraudCheckStatusFilter,
  LocationFragment,
  Primary_Transaction_Status,
} from '@occc/graphql';
import {
  array,
  custom,
  date,
  literal,
  nativeEnum,
  number,
  object,
  string,
  union,
  ZodIssueCode,
} from 'zod';
import { IS_REQUIRED } from '@occc-app/lib/constants/validationMessages';
import { isBeforeDateTime } from '@occc-app/lib/date';
import { normalizePhoneNumber } from '@occc/utils-phone-number';
import { Max32bitIntSchema } from '@occc/validation';

import { TransactionsFilterFormValues } from './transactions-filter-form.types';
import {
  FRAUD_CHECK_STATUS_ALL,
  TRANSACTION_STATUS_ALL,
  MIN_PHONE_NUMBER_LENGTH_FOR_SEARCH,
} from './transactions-filter-form.constants';

export const schema = object({
  locations: array(custom<LocationFragment>()).optional(),
  amountMin: Max32bitIntSchema(number()).optional(),
  amountMax: Max32bitIntSchema(number()).optional(),
  startDate: date().optional().nullable(),
  endDate: date().optional().nullable(),
  transactionStatus: union([
    nativeEnum(Primary_Transaction_Status),
    literal('ALL' satisfies typeof TRANSACTION_STATUS_ALL),
  ]).optional(),
  fraudCheckStatus: union([
    nativeEnum(FraudCheckStatusFilter),
    literal('ALL' satisfies typeof FRAUD_CHECK_STATUS_ALL),
  ]).optional(),
  transactionCompletedAt: nativeEnum(CompletionRange).optional(),
  phoneNumber: string()
    .refine((input) => {
      if (!input) {
        return true;
      }

      const normalizedPhoneNumber = normalizePhoneNumber(input);
      return normalizedPhoneNumber.length >= MIN_PHONE_NUMBER_LENGTH_FOR_SEARCH;
    }, `Must be at least ${MIN_PHONE_NUMBER_LENGTH_FOR_SEARCH} digits long.`)
    .optional(),
})
  .superRefine((data, ctx) => {
    if (
      typeof data.amountMin === 'number' &&
      typeof data.amountMax === 'number' &&
      data.amountMax < data.amountMin
    ) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ['amountMax' satisfies keyof TransactionsFilterFormValues],
        message: 'Max amount cannot be less than min amount.',
      });
    }
  })
  .superRefine((data, ctx) => {
    if (data.transactionCompletedAt !== CompletionRange.CustomRange) {
      return;
    }

    if (!data.startDate) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ['startDate' satisfies keyof TransactionsFilterFormValues],
        message: IS_REQUIRED,
      });
    }

    if (!data.endDate) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ['endDate' satisfies keyof TransactionsFilterFormValues],
        message: IS_REQUIRED,
      });
    }

    const isEndDateBeforeStartDate =
      data.startDate && data.endDate && isBeforeDateTime(data.endDate, data.startDate);

    if (isEndDateBeforeStartDate) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ['endDate' satisfies keyof TransactionsFilterFormValues],
        message: 'End date cannot be earlier than start date.',
      });
    }
  });
